import { computed, DestroyRef, effect, inject, Injector, PendingTasks, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import type { FormGroup } from '@angular/forms';
import { ToastService } from '../toast/toast.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { getFieldError as getFieldErrorUtil } from '../utils/form-error.utils';
import { hasAuditLogAccess } from '../utils/audit-log-access';
import { wireAuditPanelAutoOpen } from '../utils/audit-panel-auto-open';
import { SidebarNavStateService } from '../sidebar-nav/sidebar-nav-state.service';
import type { EntityCrudStrategy, EntityEditConfig, EntityItemResource, EntityMutationResource } from './entity-edit.types';
import type { ProfileDangerAction, ProfileQuickAction, ProfileStat } from '../profile-summary-card/index';
import { EditStateCoordinator } from './edit-state-coordinator.service';
import { FIELD_PERMISSION_CHECKER } from './field-permission-checker';
import { DEFAULT_TRANSLATION_LANGUAGE, isSupportedLanguageCode, resolveTranslation, type TranslatableText } from '@zag/core/common';

export abstract class EntityEditFacade<TEntity extends { id: string }, TForm extends Record<string, unknown>> {
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);
  protected readonly toast = inject(ToastService);
  protected readonly confirmDialog = inject(ConfirmDialogService);
  protected readonly translate = inject(TranslateService);
  protected readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });
  protected readonly breakpointObserver = inject(BreakpointObserver);
  protected readonly sidebarNavState = inject(SidebarNavStateService);
  protected readonly injector = inject(Injector);
  protected readonly destroyRef = inject(DestroyRef);
  private readonly pendingTasks = inject(PendingTasks);
  private readonly _coordinator = inject(EditStateCoordinator, { optional: true });
  private readonly auditPermissionChecker = inject(FIELD_PERMISSION_CHECKER, { optional: true });

  abstract readonly config: EntityEditConfig;
  abstract readonly form: FormGroup;

  abstract mapEntityToForm(entity: TEntity): Partial<TForm>;
  abstract mapFormToPayload(): unknown;
  abstract getStats(entity: TEntity | null): ProfileStat[];
  abstract getFormResetValues(): Partial<TForm>;
  protected abstract getCrudStrategy(): EntityCrudStrategy<TEntity, unknown>;

  private readonly routeParams = toSignal(this.route.paramMap);
  readonly entityId = computed(() => this.routeParams()?.get('id') ?? null);
  readonly isNew = computed(() => !this.entityId());

  // Resource is stored as a signal so that `entity`, `isLoading`, and `error`
  // computeds always track it reactively — even if evaluated before `init()`.
  private readonly _resource = signal<EntityItemResource<TEntity> | null>(null);

  readonly entity = computed(() => this._resource()?.value() ?? null);

  readonly isLoading = computed(() => {
    if (this.isNew()) return false;
    return this._resource()?.isLoading() ?? false;
  });

  readonly error = computed(() => {
    this.currentLang();
    if (this.isNew()) return null;
    if (this._resource()?.error()) {
      return this.translate.instant(`${this.config.i18nPrefix}.loadError`);
    }
    return null;
  });

  private readonly saveOperation = signal<EntityMutationResource<TEntity> | null>(null);
  private readonly saveFinalizationInProgress = signal(false);
  readonly isSaving = computed(() => (this.saveOperation()?.isLoading() ?? false) || this.saveFinalizationInProgress());

  private readonly _formValid = signal(false);
  readonly formValid = this._formValid.asReadonly();

  private readonly deleteOperation = signal<EntityMutationResource<void> | null>(null);

  readonly auditPanelOpen = signal(false);
  readonly canAccessAuditLog = computed(() => this.config.supportsAuditLog !== false && hasAuditLogAccess(this.auditPermissionChecker));

  readonly profileStats = computed(() => {
    this.currentLang(); // track lang signal so translated stat labels refresh on language switch
    return this.getStats(this.entity());
  });

  private readonly _formValues = signal<Partial<TForm>>({} as Partial<TForm>);
  readonly formValues = this._formValues.asReadonly();

  readonly quickActions = computed(() => {
    this.currentLang();
    return this.getQuickActions();
  });
  readonly dangerActions = computed(() => {
    this.currentLang();
    return this.getDangerActions();
  });

  protected resolveFormTranslation<Fields extends string>(
    translations: TranslatableText<Fields> | null | undefined,
    field: Fields,
    legacy?: string | null
  ): string | null {
    const activeLanguage = this.currentLang().lang;
    const language =
      typeof activeLanguage === 'string' && isSupportedLanguageCode(activeLanguage) ? activeLanguage : DEFAULT_TRANSLATION_LANGUAGE;

    return resolveTranslation(translations, field, language, { legacy });
  }

  private previousEntityId: string | null | symbol = Symbol('UNINITIALIZED');

  /**
   * Must be called at the end of every subclass constructor, after `form` is
   * initialized and `getCrudStrategy()` is ready.
   *
   * This method eagerly creates the item resource **outside** any reactive
   * computation context (preventing NG0602), then wires reactive subscriptions.
   * Save/delete use `effect()` to properly track mutation resource state changes.
   *
   * @example
   * ```typescript
   * constructor() {
   *   super();
   *   this.form = new FormGroup({ ... });
   *   this.init();
   * }
   * ```
   */
  protected init(): void {
    wireAuditPanelAutoOpen({
      isNew: this.isNew,
      canAccess: this.canAccessAuditLog,
      isSidebarCollapsed: this.sidebarNavState.isCollapsed,
      auditPanelOpen: this.auditPanelOpen,
      breakpointObserver: this.breakpointObserver,
      destroyRef: this.destroyRef,
      injector: this.injector,
    });

    // Eagerly initialize the item resource outside any reactive context.
    // runInInjectionContext ensures rxResource's internal use of effect() is valid.
    this._resource.set(runInInjectionContext(this.injector, () => this.getCrudStrategy().load(this.entityId)));

    // Seed _formValues and keep it in sync reactively — no Promise, no effect().
    this._formValues.set(this.form.value as Partial<TForm>);
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this._formValues.set(v as Partial<TForm>);
    });

    // Track form validity as a signal so OnPush templates react to validity changes.
    this._formValid.set(this.form.valid);
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this._formValid.set(this.form.valid);
    });

    this.syncCoordinator(this.entityId());

    // Reset form when the route entity ID changes and sync coordinator.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentId = this.entityId();
      this.syncCoordinator(currentId);

      if (currentId !== this.previousEntityId) {
        this.previousEntityId = currentId;
        this.form.reset(this.getFormResetValues());
      }
    });

    toObservable(this.entity, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(entity => {
        if (entity && !this.isNew()) {
          const patch = this.mapEntityToForm(entity);
          this.form.patchValue(patch as Record<string, unknown>);
          this.afterFormPopulated(entity);
        }
      });

    // React to save-operation completion.
    // Uses effect() so Angular tracks both the saveOperation signal AND
    // the mutation resource's internal signals (isSuccess, isComplete, error).
    // A toObservable(this.saveOperation) subscription would only re-emit when
    // the signal reference changes, missing internal state transitions.
    effect(
      () => {
        const op = this.saveOperation();
        if (!op) return;

        if (op.isSuccess()) {
          const savedEntity = op.value();
          this.saveFinalizationInProgress.set(true);
          this.saveOperation.set(null);

          this.pendingTasks.run(async () => {
            try {
              const wasNew = this.isNew();

              await Promise.resolve(this.afterSaveSuccess(savedEntity as TEntity));

              if (wasNew && savedEntity?.id) {
                await this.router.navigate([this.config.routePath, savedEntity.id, ...(this.config.editRouteSegments ?? [])]);
              }

              this.toast.success(this.translate.instant(`${this.config.i18nPrefix}.saveSuccess`));
            } catch (error) {
              const errorMessage =
                error instanceof Error && error.message ? error.message : this.translate.instant(`${this.config.i18nPrefix}.saveError`);
              this.toast.error(errorMessage);
            } finally {
              this.saveFinalizationInProgress.set(false);
            }
          });
        } else if (op.isComplete() && op.error()) {
          const error = op.error();
          const handled = this.onSaveError(error);
          if (!handled) {
            const errorMessage = error?.message ?? this.translate.instant(`${this.config.i18nPrefix}.saveError`);
            this.toast.error(errorMessage);
          }
          this.saveOperation.set(null);
          this.saveFinalizationInProgress.set(false);
        }
      },
      { injector: this.injector }
    );

    // React to delete-operation completion (same rationale as save above).
    effect(
      () => {
        const op = this.deleteOperation();
        if (!op) return;
        if (op.isSuccess()) {
          this.toast.success(this.translate.instant(`${this.config.i18nPrefix}.deleteSuccess`));
          this.afterDeleteSuccess();
          this.router.navigate([this.config.listRoutePath ?? this.config.routePath]);
          this.deleteOperation.set(null);
        } else if (op.isComplete() && op.error()) {
          const errorMessage = op.error()?.message ?? this.translate.instant(`${this.config.i18nPrefix}.deleteError`);
          this.toast.error(errorMessage);
          this.deleteOperation.set(null);
        }
      },
      { injector: this.injector }
    );
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.mapFormToPayload();
    const id = this.entityId();
    const strategy = this.getCrudStrategy();

    const mutation = id ? strategy.update(id, payload) : strategy.create(payload);
    this.saveOperation.set(mutation);
  }

  async onDelete(): Promise<void> {
    const id = this.entityId();
    const entity = this.entity();
    if (!id || !entity) return;

    const name = (entity as Record<string, unknown>)['name'] ?? '';
    const confirmMessage = this.translate.instant(`${this.config.i18nPrefix}.deleteConfirm`, { name });
    const confirmed = await this.confirmDialog.confirm({
      title: this.translate.instant('common.delete'),
      message: confirmMessage,
      confirmText: this.translate.instant('common.delete'),
      destructive: true,
    });
    if (!confirmed) return;

    const mutation = this.getCrudStrategy().delete(id);
    this.deleteOperation.set(mutation);
  }

  onCancel(): void {
    this.router.navigate([this.config.listRoutePath ?? this.config.routePath]);
  }

  getFieldError(field: string): string | null {
    const control = this.form.get(field);
    if (!control) return null;
    return getFieldErrorUtil(control, this.translate);
  }

  reload(): void {
    const resource = this._resource();
    if (resource && 'reload' in resource && typeof resource.reload === 'function') {
      resource.reload();
    }
  }

  protected onSaveError(_error: unknown): boolean {
    return false;
  }

  protected afterSaveSuccess(_saved: TEntity): Promise<void> | void {
    // Override in subclasses
  }

  protected afterDeleteSuccess(): void {
    // Override in subclasses
  }

  protected afterFormPopulated(_entity: TEntity): void {
    // Override in subclasses
  }

  private syncCoordinator(entityId: string | null): void {
    if (!this._coordinator) {
      return;
    }

    this._coordinator.entityId.set(entityId);
    this._coordinator.isNew.set(!entityId);
    this._coordinator.isEditMode.set(Boolean(entityId));
  }

  protected getQuickActions(): ProfileQuickAction[] {
    const isNew = this.isNew();
    const actions: ProfileQuickAction[] = [];

    if (this.canAccessAuditLog()) {
      actions.push({
        id: 'audit-log',
        label: this.translate.instant('common.activityLog'),
        icon: 'history',
        disabled: isNew,
      });
    }

    return actions;
  }

  protected getDangerActions(): ProfileDangerAction[] {
    if (this.config.supportsDelete === false) {
      return [];
    }

    return [
      {
        id: 'delete',
        label: this.translate.instant('common.delete'),
        icon: 'delete',
        disabled: this.isNew(),
      },
    ];
  }
}
